import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Layers, Search, Filter, Edit, Trash2, Plus, Image as ImageIcon } from "lucide-react";
import * as Switch from "@radix-ui/react-switch";
import useDebounce from "../../hooks/useDebounce";
import {
  fetchSubCategories,
  toggleSubCategoryStatus,
  deleteSubCategory,
  resetSubCategoryStatuses,
} from "../../store/slices/subCategoriesSlice";
import { fetchCategories } from "../../store/slices/commonApiSlice";
import ReusableModal, { MODAL_TYPES } from "../../components/ReusableModal";
import { showApiError, showApiSuccess } from "../../utils/toast";
import Loader from "../../components/Loader";
import Pagination from "../../components/tables/Pagination";

export default function ViewSubCategoriesPage() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const [searchTerm, setSearchTerm] = useState("");
  const debouncedSearch = useDebounce(searchTerm);
  const [filterStatus, setFilterStatus] = useState("all");
  const [filterCategory, setFilterCategory] = useState("all");
  const [modalState, setModalState] = useState({
    open: false,
    type: null,
    data: null,
    loading: false,
  });

  const {
    subCategories = [],
    pagination,
    subCategoryStatusChangeStatus,
    subCategoryDeleteStatus,
    loading,
  } = useSelector((state) => state.subCategories);

  const { categories = [] } = useSelector((state) => state.common);

  const currentPage = parseInt(searchParams.get("page") || "1", 10);
  const itemsPerPage = 10;

  // Calculate which backend page we need
  const backendPageSize = 100;
  const startRecord = (currentPage - 1) * itemsPerPage;
  const backendPage = Math.floor(startRecord / backendPageSize) + 1;

  useEffect(() => {
    dispatch(fetchCategories());
  }, [dispatch]);

  useEffect(() => {
    const params = { page: backendPage, per_page: backendPageSize };
    if (filterStatus !== "all") {
      params.status = filterStatus === "active";
    }
    if (debouncedSearch) {
      params.search = debouncedSearch;
    }
    if (filterCategory !== "all") {
      params.category_uuid = filterCategory;
    }
    dispatch(fetchSubCategories(params));
  }, [dispatch, backendPage, filterStatus, debouncedSearch, filterCategory]);

  const handlePageChange = (page) => {
    navigate(`?page=${page}`);
  };

  const openModal = (type, data) => {
    setModalState({
      open: true,
      type,
      data: { ...data, entityType: "SubCategory" },
      loading: false,
    });
  };

  const closeModal = () => {
    setModalState({
      open: false,
      type: null,
      data: null,
      loading: false,
    });
  };

  const setModalLoading = (loading) => {
    setModalState((prev) => ({ ...prev, loading }));
  };

  const handleStatusChange = async (subCategory) => {
    setModalLoading(true);
    try {
      await dispatch(toggleSubCategoryStatus(subCategory.uuid)).unwrap();
      showApiSuccess("SubCategory status updated successfully");
      closeModal();
      dispatch(fetchSubCategories({ page: backendPage, per_page: backendPageSize }));
    } catch (error) {
      setModalLoading(false);
      showApiError(null, error || "Failed to update subcategory status");
    }
  };

  const handleDeleteSubCategory = async (subCategory) => {
    setModalLoading(true);
    try {
      await dispatch(deleteSubCategory(subCategory.uuid)).unwrap();
      showApiSuccess("SubCategory deleted successfully");
      closeModal();
      dispatch(fetchSubCategories({ page: backendPage, per_page: backendPageSize }));
    } catch (error) {
      setModalLoading(false);
      showApiError(null, error || "Failed to delete subcategory");
    }
  };

  const handleModalConfirm = () => {
    if (modalState.type === MODAL_TYPES.ENABLE_DISABLE) {
      handleStatusChange(modalState.data);
    } else if (modalState.type === MODAL_TYPES.DELETE) {
      handleDeleteSubCategory(modalState.data);
    }
  };

  const handleEditSubCategory = (subCategory) => {
    navigate(`/sub-categories/add?edit=${subCategory.uuid}`);
  };

  useEffect(() => {
    if (subCategoryStatusChangeStatus === "success") {
      dispatch(resetSubCategoryStatuses());
    } else if (subCategoryStatusChangeStatus === "failed") {
      dispatch(resetSubCategoryStatuses());
    }
  }, [subCategoryStatusChangeStatus, dispatch]);

  useEffect(() => {
    if (subCategoryDeleteStatus === "success") {
      dispatch(resetSubCategoryStatuses());
    } else if (subCategoryDeleteStatus === "failed") {
      dispatch(resetSubCategoryStatuses());
    }
  }, [subCategoryDeleteStatus, dispatch]);

  const getStatusBadge = (status) => {
    return status
      ? "bg-green-100 text-green-800"
      : "bg-red-100 text-red-800";
  };

  // Client-side pagination
  const totalRecords = pagination?.total || subCategories.length;
  const totalPages = Math.ceil(totalRecords / itemsPerPage);
  const backendStartIndex = (backendPage - 1) * backendPageSize;
  const localStartIndex = startRecord - backendStartIndex;
  const localEndIndex = localStartIndex + itemsPerPage;
  const paginatedSubCategories = subCategories.slice(localStartIndex, localEndIndex);

  return (
    <div className="max-w-7xl mx-auto space-y-6">
      {loading && <Loader />}

      <div className="bg-white rounded-lg shadow p-6">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-4">
          <div>
            <h2 className="text-2xl font-bold text-gray-900">All SubCategories</h2>
            <p className="text-sm text-gray-500 mt-1">
              Manage your subcategories linked to categories
            </p>
          </div>
          <button
            onClick={() => navigate("/sub-categories/add")}
            className="mt-4 sm:mt-0 bg-blue-600 text-white px-6 py-2 rounded-lg hover:bg-blue-700 transition-colors flex items-center space-x-2"
          >
            <Plus className="h-4 w-4" />
            <span>Add New SubCategory</span>
          </button>
        </div>

        {/* Search and Filter */}
        <div className="flex flex-col sm:flex-row gap-4">
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
            <input
              type="text"
              placeholder="Search subcategories..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
          <div className="relative">
            <Filter className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
            <select
              value={filterCategory}
              onChange={(e) => setFilterCategory(e.target.value)}
              className="pl-10 pr-8 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="all">All Categories</option>
              {categories.map((category) => (
                <option key={category.uuid} value={category.uuid}>
                  {category.name}
                </option>
              ))}
            </select>
          </div>
          <div className="relative">
            <Filter className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
            <select
              value={filterStatus}
              onChange={(e) => setFilterStatus(e.target.value)}
              className="pl-10 pr-8 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="all">All Status</option>
              <option value="active">Active</option>
              <option value="inactive">Inactive</option>
            </select>
          </div>
        </div>
      </div>

      {/* SubCategories Table */}
      <div className="bg-white rounded-lg shadow overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50">
              <tr>
                {/* <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                  Icon
                </th> */}
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                  Name
                </th>
                {/* <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                  Tagline
                </th> */}
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                  Category
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                  Slug
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                  Status
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {paginatedSubCategories.length > 0 ? (
                paginatedSubCategories.map((subCategory) => (
                  <tr key={subCategory.uuid} className="hover:bg-gray-50 transition-colors">
                    {/* <td className="px-6 py-4">
                      {subCategory.icon ? (
                        <img
                          src={subCategory.icon}
                          alt={subCategory.name}
                          className="w-10 h-10 rounded object-cover"
                        />
                      ) : (
                        <div className="w-10 h-10 rounded bg-gray-200 flex items-center justify-center">
                          <ImageIcon className="h-5 w-5 text-gray-400" />
                        </div>
                      )}
                    </td> */}
                    <td className="px-6 py-4 font-medium text-gray-900">
                      {subCategory.name}
                    </td>
                    {/* <td className="px-6 py-4 text-gray-500">
                      {subCategory.tagline ? (
                        <span className="text-sm">{subCategory.tagline}</span>
                      ) : (
                        <span className="text-sm text-gray-400 italic">No tagline</span>
                      )}
                    </td> */}
                    <td className="px-6 py-4">
                      <span className="px-2 py-1 text-xs rounded-full bg-blue-100 text-blue-800">
                        {subCategory.category?.name || "N/A"}
                      </span>
                    </td>
                    <td className="px-6 py-4 text-gray-500">{subCategory.slug}</td>
                    <td className="px-6 py-4">
                      <span
                        className={`px-2 py-1 text-xs rounded-full ${getStatusBadge(
                          subCategory.status
                        )}`}
                      >
                        {subCategory.status ? "Active" : "Inactive"}
                      </span>
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex items-center space-x-3">
                        <Switch.Root
                          id={`subcategory-status-${subCategory.uuid}`}
                          checked={subCategory.status}
                          onCheckedChange={() =>
                            openModal(MODAL_TYPES.ENABLE_DISABLE, subCategory)
                          }
                          className="w-[42px] h-[25px] bg-gray-200 rounded-full relative shadow-sm 
                               data-[state=checked]:bg-blue-600 outline-none cursor-pointer"
                        >
                          <Switch.Thumb
                            className="block w-[21px] h-[21px] bg-white rounded-full transition-transform 
                                 translate-x-0.5 will-change-transform 
                                 data-[state=checked]:translate-x-[19px]"
                          />
                        </Switch.Root>

                        <div className="flex space-x-2">
                          <button
                            onClick={() => handleEditSubCategory(subCategory)}
                            className="text-green-600 hover:text-green-800 p-1 rounded hover:bg-green-50"
                            title="Edit SubCategory"
                          >
                            <Edit className="h-4 w-4" />
                          </button>
                          {/* <button
                            onClick={() =>
                              openModal(MODAL_TYPES.DELETE, subCategory)
                            }
                            className="text-red-600 hover:text-red-800 p-1 rounded hover:bg-red-50"
                            title="Delete SubCategory"
                          >
                            <Trash2 className="h-4 w-4" />
                          </button> */}
                        </div>
                      </div>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td
                    colSpan={5}
                    className="px-6 py-12 text-center text-gray-500"
                  >
                    <Layers className="mx-auto h-12 w-12 text-gray-300 mb-4" />
                    <p className="text-lg font-medium mb-2">
                      No subcategories found
                    </p>
                    <p className="text-sm">
                      {searchTerm || filterStatus !== "all" || filterCategory !== "all"
                        ? "Try adjusting your search or filters"
                        : "Get started by creating your first subcategory"}
                    </p>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <Pagination
          currentPage={currentPage}
          totalPages={totalPages}
          totalItems={totalRecords}
          itemsPerPage={itemsPerPage}
          onPageChange={handlePageChange}
        />
      )}

      {/* Reusable Modal */}
      <ReusableModal
        open={modalState.open}
        onOpenChange={closeModal}
        type={modalState.type}
        data={modalState.data}
        onConfirm={handleModalConfirm}
        loading={modalState.loading}
      />
    </div>
  );
}
